import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Checkbox, Text } from '../../../../components/common';
import { HelpLink } from '../../../../components/common/HelpLink';
import { HELP_LINKS } from '../../../../constants/HelpLinks';
import { ModalName } from '../../../../constants/ModalName';
import { Source } from '../../../../constants/Source';
import styles from '../../../../containers/FilesManager/FilesManager.scss';
import { MediaManagerSDKAnyItem } from '../../../media-manager-sdk';
import {
  ClickOrigin as ClickOriginLearnMore,
  useTrackLearnMoreClickAnalytics,
} from '../../bi/useTrackLearnMoreClickAnalytics';
import { IParams } from '../../bi/useTrackTrashActions';
import { useSources } from '../../useSources';
import { useModal, useModalCheckboxState, useModalHandlersAndOptions } from '../index';
import { Theme } from '../useModal.types';

interface IUsePermanentDeleteModal {
  selectedItemsCount: number;
  trackDeletedForever: (items: MediaManagerSDKAnyItem[], params: IParams) => void;
  selectedItems: MediaManagerSDKAnyItem[];
  deleteForever: () => void;
  trackEmptyTrash: (params: IParams) => void;
  emptyTrash: () => void;
}

export const usePermanentDeleteModal = ({
  selectedItemsCount,
  trackDeletedForever,
  selectedItems,
  deleteForever,
  trackEmptyTrash,
  emptyTrash,
}: IUsePermanentDeleteModal) => {
  const { trackLearnMoreClick } = useTrackLearnMoreClickAnalytics();

  const handleHelpLinkClick = useCallback(() => {
    trackLearnMoreClick(ClickOriginLearnMore.PermanentlyDeletePopUp);
  }, [trackLearnMoreClick]);

  const { formatMessage } = useIntl();

  return useModal(
    ModalName.PermanentDelete,
    useModalCheckboxState(({ isChecked, resetCheckbox, handleCheckboxChange }) => ({
      skin: Theme.Destructive,
      image: <div className={styles.deleteBoardImage} />,
      title: formatMessage(
        {
          id: 'files-manager.delete-permanently-modal-title',
        },
        { itemsCount: selectedItemsCount },
      ),
      content: (
        <Text>
          <FormattedMessage
            id="files-manager.delete-permanently-modal-content"
            values={{
              itemsCount: selectedItemsCount,
              learnMoreLink: (
                <HelpLink
                  article={HELP_LINKS.DELETE_FLOW_READ_MORE}
                  size="small"
                  onClick={handleHelpLinkClick}
                  dataHook="help-link-permanent-delete"
                >
                  <FormattedMessage id="files-manager.delete-permanently-modal-learn-more" />
                </HelpLink>
              ),
            }}
          />
        </Text>
      ),
      sideActions: (
        <Checkbox checked={isChecked} onChange={handleCheckboxChange} dataHook="checkbox-confirm-delete-permanently">
          <Text size="small" weight="thin">
            {formatMessage({ id: 'files-manager.delete-permanently-modal-checkbox' })}
          </Text>
        </Checkbox>
      ),
      confirmText: formatMessage({ id: 'common.cancel' }),
      cancelText: formatMessage({ id: selectedItemsCount ? 'actions.delete-permanently' : 'actions.empty-trash' }),
      disableCancel: !isChecked,
      onOk: resetCheckbox,
      onClose: resetCheckbox,
      onCancel: ({ additionalOptions: { clickOrigin } }) => {
        resetCheckbox();

        if (selectedItemsCount) {
          trackDeletedForever(selectedItems, { clickOrigin });
          deleteForever();
          return;
        }

        trackEmptyTrash({ clickOrigin });
        emptyTrash();
      },
    })),
  );
};

export const usePermanentDeleteModalHandlers = () => useModalHandlersAndOptions(ModalName.PermanentDelete);

export const useShouldShowPermanentDelete = (source: Source) => {
  const { isTrash } = useSources({ source });

  return isTrash;
};
